import { Save } from "lucide-react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

import type { ProductPlan } from "../types";

export const ProductPlanSettings = () => {
  const { data, refetch } = useGetList<ProductPlan>("product_plans", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "display_order", order: "ASC" },
  });
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  useEffect(() => {
    if (data) setPlans(data);
  }, [data]);

  const updatePlan = (id: ProductPlan["id"], changes: Partial<ProductPlan>) => {
    setPlans((current) =>
      current.map((plan) => (plan.id === id ? { ...plan, ...changes } : plan)),
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(
        plans.map((plan) =>
          dataProvider.update("product_plans", {
            id: plan.id,
            data: {
              name: plan.name,
              price: Number(plan.price),
              currency: plan.currency,
              billing_interval: plan.billing_interval,
              active: plan.active,
              display_order: Number(plan.display_order),
            },
            previousData: plan,
          }),
        ),
      );
      notify("Planes B2C guardados");
      refetch();
    } catch {
      notify("No fue posible guardar los planes B2C", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!plans.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Los planes aparecerán después de aplicar la migración del funnel.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {plans.map((plan) => (
        <div
          key={plan.id}
          className="grid grid-cols-1 gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_8rem_6rem_auto]"
        >
          <div>
            <label className="text-xs text-muted-foreground">Nombre</label>
            <input
              className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={plan.name}
              onChange={(event) =>
                updatePlan(plan.id, { name: event.target.value })
              }
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Precio</label>
            <input
              type="number"
              min={0}
              className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={plan.price}
              onChange={(event) =>
                updatePlan(plan.id, { price: Number(event.target.value) })
              }
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Moneda</label>
            <select
              className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={plan.currency}
              onChange={(event) =>
                updatePlan(plan.id, {
                  currency: event.target.value as ProductPlan["currency"],
                })
              }
            >
              <option value="CLP">CLP</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={plan.active}
              onChange={(event) =>
                updatePlan(plan.id, { active: event.target.checked })
              }
            />
            Activo
          </label>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={save} disabled={saving}>
        <Save className="mr-2 h-4 w-4" />
        {saving ? "Guardando..." : "Guardar planes B2C"}
      </Button>
    </div>
  );
};

